<script setup>
import { ref, watch, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { isIpInAllowlist } from 'dashboard/helpers/ipAllowlist';
import Button from 'next/button/Button.vue';
import Switch from 'next/switch/Switch.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SectionLayout from './SectionLayout.vue';

const { t } = useI18n();
const store = useStore();
const { currentAccount, updateAccount } = useAccount();

const enabled = ref(false);
const allowedIpsText = ref('');
const allowedApiIpsText = ref('');
const saving = ref(false);
const confirmDialogRef = ref(null);

const parseIpText = text =>
  text
    .split(/[\n,]+/)
    .map(ip => ip.trim())
    .filter(Boolean);

const syncFromAccount = () => {
  const lock = currentAccount.value?.settings?.access_lock || {};
  enabled.value = Boolean(lock.enabled);
  allowedIpsText.value = Array.isArray(lock.allowed_ips)
    ? lock.allowed_ips.join('\n')
    : '';
  allowedApiIpsText.value = Array.isArray(lock.allowed_api_ips)
    ? lock.allowed_api_ips.join('\n')
    : '';
};

watch(
  currentAccount,
  () => {
    // Don't clobber in-progress edits while a save is in flight.
    if (saving.value) return;
    syncFromAccount();
  },
  { deep: true, immediate: true }
);

onMounted(() => {
  // Refresh so client_ip is present for the lock-out confirmation check.
  store.dispatch('accounts/get', { silent: true });
});

const parsedIps = computed(() => parseIpText(allowedIpsText.value));
const parsedApiIps = computed(() => parseIpText(allowedApiIpsText.value));

const clientIp = computed(() => currentAccount.value?.client_ip || '');

const wouldLockOutCurrentIp = computed(() => {
  if (!enabled.value) return false;
  if (!clientIp.value) return true;
  return !isIpInAllowlist(clientIp.value, parsedIps.value);
});

const confirmDescription = computed(() =>
  t('GENERAL_SETTINGS.ACCESS_LOCK.CONFIRM_LOCKOUT.DESCRIPTION', {
    ip: clientIp.value || t('GENERAL_SETTINGS.ACCESS_LOCK.CONFIRM_LOCKOUT.UNKNOWN_IP'),
  })
);

const persistSettings = async () => {
  saving.value = true;
  try {
    await updateAccount({
      access_lock: {
        enabled: enabled.value,
        allowed_ips: parsedIps.value,
        allowed_api_ips: parsedApiIps.value,
      },
    });
    syncFromAccount();
    useAlert(t('GENERAL_SETTINGS.ACCESS_LOCK.SAVE_SUCCESS'));
  } catch {
    useAlert(t('GENERAL_SETTINGS.ACCESS_LOCK.SAVE_ERROR'));
  } finally {
    saving.value = false;
  }
};

const handleSave = async () => {
  if (wouldLockOutCurrentIp.value) {
    confirmDialogRef.value?.open();
    return;
  }
  await persistSettings();
};

const handleConfirmLockout = async () => {
  confirmDialogRef.value?.close();
  await persistSettings();
};
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.ACCESS_LOCK.TITLE')"
    :description="t('GENERAL_SETTINGS.ACCESS_LOCK.DESCRIPTION')"
  >
    <div class="flex flex-col gap-4">
      <label
        class="flex cursor-pointer items-start gap-3 text-sm text-slate-700 dark:text-slate-300"
      >
        <Switch v-model="enabled" />
        <span>
          <span class="block font-medium">
            {{ t('GENERAL_SETTINGS.ACCESS_LOCK.ENABLED') }}
          </span>
          <span class="mt-0.5 block text-slate-500 dark:text-slate-400">
            {{ t('GENERAL_SETTINGS.ACCESS_LOCK.ENABLED_HELP') }}
          </span>
        </span>
      </label>

      <div>
        <label
          class="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
        >
          {{ t('GENERAL_SETTINGS.ACCESS_LOCK.ALLOWED_IPS') }}
        </label>
        <textarea
          v-model="allowedIpsText"
          rows="5"
          class="reset-base w-full border border-slate-200 rounded-lg bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
          :placeholder="
            t('GENERAL_SETTINGS.ACCESS_LOCK.ALLOWED_IPS_PLACEHOLDER')
          "
        />
        <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {{ t('GENERAL_SETTINGS.ACCESS_LOCK.ALLOWED_IPS_HELP') }}
        </p>
        <p
          v-if="clientIp"
          class="mt-1 text-xs text-slate-500 dark:text-slate-400"
        >
          {{
            t('GENERAL_SETTINGS.ACCESS_LOCK.CURRENT_IP', { ip: clientIp })
          }}
        </p>
      </div>

      <div>
        <label
          class="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
        >
          {{ t('GENERAL_SETTINGS.ACCESS_LOCK.ALLOWED_API_IPS') }}
        </label>
        <textarea
          v-model="allowedApiIpsText"
          rows="5"
          class="reset-base w-full border border-slate-200 rounded-lg bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
          :placeholder="
            t('GENERAL_SETTINGS.ACCESS_LOCK.ALLOWED_API_IPS_PLACEHOLDER')
          "
        />
        <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {{ t('GENERAL_SETTINGS.ACCESS_LOCK.ALLOWED_API_IPS_HELP') }}
        </p>
      </div>

      <div>
        <Button blue :is-loading="saving" @click="handleSave">
          {{ t('GENERAL_SETTINGS.ACCESS_LOCK.SAVE') }}
        </Button>
      </div>
    </div>

    <Dialog
      ref="confirmDialogRef"
      type="alert"
      width="md"
      :title="t('GENERAL_SETTINGS.ACCESS_LOCK.CONFIRM_LOCKOUT.TITLE')"
      :description="confirmDescription"
      :confirm-button-label="
        t('GENERAL_SETTINGS.ACCESS_LOCK.CONFIRM_LOCKOUT.CONFIRM')
      "
      :cancel-button-label="
        t('GENERAL_SETTINGS.ACCESS_LOCK.CONFIRM_LOCKOUT.CANCEL')
      "
      :is-loading="saving"
      @confirm="handleConfirmLockout"
    />
  </SectionLayout>
</template>
